import type { GithubRunnerScope, RunnerPlatform } from '@/api/runners';

export const PLATFORM_LABELS: Record<RunnerPlatform, string> = {
  GITLAB: 'GitLab',
  GITHUB: 'GitHub',
};

/** A runner without a platform predates GitHub support and is a GitLab runner. */
export function runnerPlatform(runner: { platform?: string | null }): RunnerPlatform {
  return runner.platform?.toUpperCase() === 'GITHUB' ? 'GITHUB' : 'GITLAB';
}

export const GITHUB_MAX_CONCURRENCY = 32;

export const GITHUB_TOKEN_PAGE = 'https://github.com/settings/personal-access-tokens/new';

export function githubScopeUrl(github: Pick<GithubRunnerScope, 'owner' | 'repository'>): string {
  const owner = encodeURIComponent(github.owner);
  return github.repository
    ? `https://github.com/${owner}/${encodeURIComponent(github.repository)}`
    : `https://github.com/${owner}`;
}

// The API's rules for GitHub settings (see RunnerPlatformRequests.kt and CiPlatforms.kt in the backend).
const OWNER = /^[A-Za-z0-9][A-Za-z0-9-]{0,38}$/;
const REPOSITORY = /^[A-Za-z0-9._-]{1,100}$/;
const TOKEN = /^[A-Za-z0-9_]{20,255}$/;
// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u001f\u007f-\u009f]/;
const MAX_RUNNER_GROUP_LENGTH = 100;
const MAX_LABEL_LENGTH = 256;

export function validateGithubOwner(value: string): string {
  const owner = value.trim();
  if (!owner) return 'Owner is required.';
  return OWNER.test(owner) ? '' : 'Enter a GitHub user or organization name.';
}

export function validateGithubRepository(value: string): string {
  const repository = value.trim();
  if (!repository) return 'Repository is required.';
  return REPOSITORY.test(repository) && repository !== '.' && repository !== '..'
    ? ''
    : 'Enter a GitHub repository name: letters, digits, ".", "-" and "_".';
}

export function validateGithubRunnerGroup(value: string): string {
  const group = value.trim();
  return group.length > MAX_RUNNER_GROUP_LENGTH || CONTROL.test(group)
    ? `Use at most ${MAX_RUNNER_GROUP_LENGTH} characters.`
    : '';
}

/** An empty token is valid; the caller decides whether one is required. */
export function validateGithubToken(value: string): string {
  const token = value.trim();
  return !token || TOKEN.test(token) ? '' : 'Enter a GitHub fine-grained personal access token.';
}

export function validateGithubConcurrency(value: number): string {
  return Number.isInteger(value) && value >= 1 && value <= GITHUB_MAX_CONCURRENCY
    ? ''
    : `GitHub runners run 1 to ${GITHUB_MAX_CONCURRENCY} jobs at a time.`;
}

/** GitHub takes the labels comma-separated; the backend checks the Hetzner label rules. */
export function validateGithubLabels(labels: string[]): string {
  return labels.some(
    (label) => label.length > MAX_LABEL_LENGTH || label.includes(',') || CONTROL.test(label)
  )
    ? `GitHub runner labels have at most ${MAX_LABEL_LENGTH} characters and no commas.`
    : '';
}
